/**
 * AcceptInvitePage — принятие приглашения и регистрация.
 *
 * URL: /accept-invite?token=xxx
 * После успешной регистрации → redirect в /inbox
 */

import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAcceptInvite } from '../api/auth';
import { AuthLayout, Button, Input, Spinner } from '../components/ui';
import { getErrorMessage } from '../lib/error-messages';

export function AcceptInvitePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const acceptInvite = useAcceptInvite();

  const handleSubmit = async () => {
    if (!token) {
      setError('Отсутствует токен приглашения');
      return;
    }
    if (!login.trim()) {
      setError('Введите логин');
      return;
    }
    if (login.trim().length < 3) {
      setError('Логин должен быть не менее 3 символов');
      return;
    }
    if (!password) {
      setError('Введите пароль');
      return;
    }
    if (password.length < 8) {
      setError('Пароль должен быть не менее 8 символов');
      return;
    }
    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setError('');
    try {
      await acceptInvite.mutateAsync({ token, login: login.trim(), password });
      navigate('/inbox', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !acceptInvite.isPending) {
      handleSubmit();
    }
  };

  // Нет токена — показываем ошибку
  if (!token) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-comp bg-accent-danger/10 border border-accent-danger/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-accent-danger font-bold text-2xl">!</span>
          </div>
          <h1 className="font-heading text-xl font-semibold text-text-primary mb-2">
            Недействительная ссылка
          </h1>
          <p className="text-text-secondary text-sm mb-6">
            Ссылка приглашения не содержит токен. Запросите новое приглашение у администратора.
          </p>
          <Link
            to="/login"
            className="text-accent-brand hover:underline text-sm font-medium"
          >
            Перейти к входу
          </Link>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout
      subtitle="Создание аккаунта"
      footer={
        <p className="text-text-secondary text-xs">
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-accent-brand hover:underline">
            Войти
          </Link>
        </p>
      }
    >
      <>
          <h2 className="font-heading text-xl font-semibold text-text-primary mb-2">
            Принять приглашение
          </h2>
          <p className="text-text-secondary text-sm mb-6">
            Создайте логин и пароль для входа в систему
          </p>

          <div className="space-y-4" onKeyDown={handleKeyDown}>
            <Input
              label="Логин"
              placeholder="Минимум 3 символа"
              value={login}
              onChange={setLogin}
              disabled={acceptInvite.isPending}
              autoComplete="username"
              autoFocus
            />

            <Input
              label="Пароль"
              placeholder="Минимум 8 символов"
              type="password"
              value={password}
              onChange={setPassword}
              disabled={acceptInvite.isPending}
              autoComplete="new-password"
            />

            <Input
              label="Подтверждение пароля"
              placeholder="Повторите пароль"
              type="password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              error={error}
              disabled={acceptInvite.isPending}
              autoComplete="new-password"
            />

            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSubmit}
              disabled={acceptInvite.isPending || !login.trim() || !password || !confirmPassword}
            >
              {acceptInvite.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Создание аккаунта...
                </span>
              ) : (
                'Создать аккаунт'
              )}
            </Button>
          </div>
      </>
    </AuthLayout>
  );
}
